import network from '../network/network';
import { getHostName } from './domain';

import {
  isDebug,
  isDebugTopAd,
  isNotNativeAd,
  isNativeAd,
  adHasExistingContainer,
  isMainAndCategoryPage,
  clone,
} from '../helpers/utils';
import amaLog from '../helpers/log';
import { getCookie, setCookie } from '../storage/cookies';

let savedAccConfig: IAdsConfig | undefined;
const highestIds: HighestIds = {
  responsive: 0,
  intext_ad: 0,
};

export const modifyMainCategoryPlacementsDivs = (processedDivIds: Set<string>): void => {
  document.querySelectorAll<HTMLElement>("[id^='responsive_'], [id^='intext_ad_']").forEach((div) => {
    if (processedDivIds.has(div.id)) return;

    const match = div.id.match(/(responsive|intext_ad)_(\d+)(-.+)?/);
    if (!match) return;

    const [_, type, , suffix = ''] = match;

    highestIds[type] += 1;
    const newId = `${type}_${highestIds[type]}${suffix}`;

    div.id = newId;
    div.setAttribute('data-aller-ad-placement', highestIds[type] >= 9 ? `${type}_X` : newId.split('-')[0]);
    processedDivIds.add(div.id);
  });
};

export const withinTopScrollerHourlyLimit = (increment: boolean = false): boolean => {
  const cookieName = 'aller_top_ad';
  const now = new Date();
  const hourKey = `${now.toISOString().split('T')[0]}-${now.getHours()}`; 
  // e.g. "2025-09-02-14"

  let cookieData: { hour: string; count: number } = { hour: hourKey, count: 0 };

  try {
    const existing = getCookie(cookieName);
    if (existing) {
      const parsed = typeof existing === 'string' ? JSON.parse(existing) : existing;
      if (parsed.hour === hourKey) {
        cookieData.count = parsed.count || 0;
      }
    }
  } catch (error) {
    amaLog('Error parsing hourly cookie data:', error);
  }

  const withinHourlyLimit = cookieData.count < 3;

  if (increment && withinHourlyLimit) {
    cookieData.count += 1;
    setCookie(cookieName, JSON.stringify(cookieData));
  }

  return withinHourlyLimit;
};


const modifyArticlesPlacementsDivs = (divId: string, placement: string) => {
  let placementDivs = document.getElementById(divId);
  if (placementDivs) {
    placementDivs.setAttribute('data-aller-ad-placement', placement);
  }
};

const fetchAdsConfig = async () => {
  const safeHost = getHostName();
  const fallbackUri = `${process.env.FALLBACK_CONFIG_BASE_URL}${safeHost}.json`;
  const resource = `/config/get${safeHost}`;
  let response;
  try {
    response = await network.get(resource);
  } catch (e) {
    console.warn('Error while loading ad configuration. Applying fallback', isDebug() ? e : '');
    response = await network.get(fallbackUri);
  }
  return response;
};

const getModifiedConfig = async (serverLoadedConfig?: IAdsConfig ) => {
  savedAccConfig =
  savedAccConfig ||
    (serverLoadedConfig && Object.keys(serverLoadedConfig).length > 0 ? serverLoadedConfig : await getConfig());

  amaLog(`ACC Config ${!!serverLoadedConfig ? "(serverLoadedConfig)" : ''}:`, savedAccConfig);

  if (!(savedAccConfig?.ads && savedAccConfig.ads.length > 0)) {
    throw new Error('Error ACC config is missing');
  }

  return savedAccConfig && clone(savedAccConfig);
};

const getConfig = async () => {
  try {
    return await fetchAdsConfig();
  } catch (e) {
    console.error('Failed to retrieve ads configuration.');
  }
};

const addExtraPlacementsToConfig = (config: any, placement: string) => {
  amaLog('addExtraPlacements if needed ', placement);

  const ads = [...config.ads];
  placement = `${placement}${placement.includes('intext') ? '_ad' : ''}`;
  const xSlot = ads.find((adSlot: any) => {
    const target = adSlot.target;
    return target.includes(`${placement}`) && (target.includes('-x') || target.includes('_x'));
  });

  const placementDivs: any[] | NodeListOf<Element> = placement
    ? document.querySelectorAll(`[id^="${placement}"]`) || []
    : [];

  if (xSlot && placementDivs.length > 0) {
    for (let no = 0; no < placementDivs.length; no++) {
      const target = placementDivs[no].id;
      const newTarget: String = target && target.includes(placement) && target.substring(0, target.indexOf('-'));
      const slotExists = ads.some((adSlot: any) => adSlot.target === `${newTarget}`);

      if (slotExists) {
        // Slot already exists. Will not add;
        continue;
      }
      const newPlacement = `${placement}_X`;

      if (newTarget) {
        amaLog(`Adding target ${newTarget} and placement ${newPlacement} from ${target}. div-id: ${placementDivs[no].id}`);

        ads.push({
          ...xSlot,
          target: newTarget,
          placement: newPlacement,
        });
      } else {
        amaLog(`Couldn't set intext_ad_X for ${target}, on index ${no + 1}`);
      }
    }
  } else {
    amaLog(`${placementDivs.length} ${placement} found, so no ${placement}_X will be set`);
  }
  amaLog('ads after adding extra:', ads);

  return { ...config, ads };
};

const modifyPlacementsInConfig = (config: any, pageNo: number) => {
  if (!config?.ads) return config;

  const modifiedConfig = clone(config);

  modifiedConfig.ads = modifiedConfig.ads.map((ad: any) => {
    const { placement, target } = ad;

    if (
      placement &&
      pageNo > 2 &&
      !isMainAndCategoryPage() &&
      /^(intext|responsive)/.test(placement)
    ) {
      const placementType = placement.startsWith("intext") ? "intext_ad" : "responsive";
      ad.placement = `${placementType}_X`;

      const divId = `${target}-${window.ama?.adsUniqueId}`;
      modifyArticlesPlacementsDivs(divId, ad.placement);
    }
    return ad;
  });
  return modifiedConfig;
};

type Config = {
  ads: any[];
};

const loadAccConfig = async (serverLoadedConfig?: IAdsConfig ) => {

  amaLog(`loadAccConfig serverLoadedConfig:`, typeof serverLoadedConfig);

  const savedAccConfig = typeof serverLoadedConfig === 'object' && Object.keys(serverLoadedConfig).length > 0 ? serverLoadedConfig : await getConfig();

  amaLog(`ACC Config ${!!serverLoadedConfig ? "(serverLoadedConfig)" : ''}:`, savedAccConfig);

  if (!(savedAccConfig?.ads && savedAccConfig.ads.length > 0)) {
    throw new Error('Error ACC config is missing');
  }
  return savedAccConfig;
};


const applyFilter = (config: Config, predicate: (ad: any) => boolean) => {
  const filteredAds = config.ads.filter(predicate);
  return { ...config, ads: filteredAds };
};

const filter = (config: any, isNative = false) => {
  //  TODO:Delete enableWallpaperAds after added enableTopAds in the database
  const shouldShowTopAd = (withinTopScrollerHourlyLimit() || isDebugTopAd()) &&
    (config?.enableWallpaperAds === 'true' || config?.enableTopAds === 'true' || isDebugTopAd());

  config.ads = shouldShowTopAd
    ? config.ads
    : config.ads.filter((ad: any) => ad.placement !== 'top_ad');

  const nativePredicate = isNative ? isNativeAd : isNotNativeAd;
  const filters = [nativePredicate, adHasExistingContainer];
  return filters.reduce((updatedConfig, filter) => applyFilter(updatedConfig, filter), config);
};

const adConfig = {
  get: getConfig,
  getModifiedConfig: getModifiedConfig,
  getModifiedPlacements: modifyPlacementsInConfig,
  loadAccConfig,
  addExtraPlacementsToConfig,
  filter: filter,
};

export default adConfig;
